import React, { useMemo, useEffect } from "react";
import "../styles/main.css";

// Helper for Indian Number to Words
const numberToWords = (num) => {
  if (!num || isNaN(num)) return "Zero";
  const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const inWords = (n) => {
    if ((n = n.toString()).length > 9) return "Overflow";
    let nArray = ("000000000" + n).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!nArray) return "";
    let str = "";
    str += (Number(nArray[1]) !== 0) ? (a[Number(nArray[1])] || b[nArray[1][0]] + " " + a[nArray[1][1]]) + "Crore " : "";
    str += (Number(nArray[2]) !== 0) ? (a[Number(nArray[2])] || b[nArray[2][0]] + " " + a[nArray[2][1]]) + "Lakh " : "";
    str += (Number(nArray[3]) !== 0) ? (a[Number(nArray[3])] || b[nArray[3][0]] + " " + a[nArray[3][1]]) + "Thousand " : "";
    str += (Number(nArray[4]) !== 0) ? (a[Number(nArray[4])] || b[nArray[4][0]] + " " + a[nArray[4][1]]) + "Hundred " : "";
    str += (Number(nArray[5]) !== 0) ? ((str !== "") ? "and " : "") + (a[Number(nArray[5])] || b[nArray[5][0]] + " " + a[nArray[5][1]]) : "";
    return str.trim();
  };

  let wholeNumber = Math.floor(num);
  let decimal = Math.round((num - wholeNumber) * 100);
  let result = inWords(wholeNumber) + " Rupees";
  if (decimal > 0) result += " and " + inWords(decimal) + " Paise";
  return result + " Only";
};

// Date & Time Formatters
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
};

const formatIndianTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit", 
        minute: "2-digit",
        hour12: true
      });
    }
  } catch (e) {
    return "";
  }
  return "";
};

const MedicineSaleReceiptModal = ({ show, receipt, onClose, onPrint }) => {
  const company = receipt?.company || {};
  const customer = receipt?.customer || {};

  useEffect(() => {
    if (show) {
      document.body.classList.add("print-modal-open");
    } else {
      document.body.classList.remove("print-modal-open");
    }
    return () => document.body.classList.remove("print-modal-open");
  }, [show]);

  const formatQty = (leaf, pc) => {
    const l = parseInt(leaf) || 0;
    const p = parseInt(pc) || 0;
    let str = "";
    if (l > 0) str += `${l}L `;
    if (p > 0) str += `${p}P`;
    return str.trim() || "0";
  };

  // ✅ LOGIC: Max items per page
  const ITEMS_PER_PAGE = 50;
  const pages = useMemo(() => {
    const pgs = [];
    let runningTotal = 0;
    const itemsToProcess = receipt?.items || [];

    for (let i = 0; i < itemsToProcess.length; i += ITEMS_PER_PAGE) {
      const pageItems = itemsToProcess.slice(i, i + ITEMS_PER_PAGE);
      const pageTotal = pageItems.reduce((sum, item) => sum + (Number(item.netAmount) || 0), 0);
      const previousTotal = runningTotal;
      runningTotal += pageTotal;

      pgs.push({
        items: pageItems,
        previousTotal,
        currentTotal: runningTotal,
        isLastPage: (i + ITEMS_PER_PAGE) >= itemsToProcess.length,
        pageNumber: Math.floor(i / ITEMS_PER_PAGE) + 1,
        totalPages: Math.ceil(itemsToProcess.length / ITEMS_PER_PAGE),
      });
    }

    if (pgs.length === 0) {
      pgs.push({ items: [], previousTotal: 0, currentTotal: 0, isLastPage: true, pageNumber: 1, totalPages: 1 });
    }
    return pgs;
  }, [receipt?.items]);

  if (!show || !receipt) return null;

  const handlePrint = () => {
    if (onPrint && typeof onPrint === 'function') {
      onPrint();
    } else {
      window.print();
    }
  };

  return (
    <>
      <style>{`
        /* =============================================
           ✅ SENIOR DEV LEVEL PRINT CSS FIX
        ============================================= */
        @media print {
        @page { margin: 2mm 5mm 5mm 5mm; size: A4 portrait; }

          body * { visibility: hidden; }

          body, html {
            background-color: #fff !important;
            margin: 0 !important; padding: 0 !important;
            height: auto !important; min-height: 100vh !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .printable-invoice, .printable-invoice * { visibility: visible !important; }

          .printable-invoice {
            position: absolute; left: 0; top: 0; width: 100%;
            margin: 0 !important; padding: 0 !important; background: white !important;
          }

          .modal, .rm-overlay {
            position: static !important; background: none !important;
            padding: 0 !important; display: block !important; overflow: visible !important;
          }
          
          .rm-card, .modal-dialog, .modal-content {
            max-width: 100% !important; width: 100% !important;
            margin: 0 !important; padding: 0 !important;
            border: none !important; box-shadow: none !important;
            background: transparent !important; display: block !important;
          }

          .no-print, .modal-backdrop, .rm-header, .rm-footer { display: none !important; }

          /* ✅ Page breaks dynamically instead of forcing a fixed height */
          .page-break {
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            position: relative;
            height: auto !important; /* DYNAMIC HEIGHT */
            min-height: 100% !important;
            width: 100% !important;
            background: white !important;
            margin: 0 !important; padding: 0 !important; box-shadow: none !important;
          }
          .page-break:last-child {
            page-break-after: auto !important; break-after: auto !important;
          }
        }

        /* =============================================
           MODAL OVERLAY (SCREEN ONLY)
        ============================================= */
        @media screen {
          .rm-overlay {
            position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65);
            z-index: 1050; display: flex; align-items: flex-start; justify-content: center;
            padding: 16px; overflow-y: auto;
          }

          .rm-card {
            display: flex; flex-direction: column; width: 100%; max-width: 900px;
            background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 40px rgba(0,0,0,0.35);
            margin: auto;
          }

          .rm-header {
            flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
            padding: 10px 16px; background: #f8f9fa; border-bottom: 1px solid #dee2e6;
          }

          .rm-body { flex: 1 1 auto; background: #e9ecef; padding: 16px; }

          .rm-footer {
            flex: 0 0 auto; display: flex; align-items: center; justify-content: flex-end; gap: 10px;
            padding: 10px 16px; background: #f8f9fa; border-top: 2px solid #dee2e6; z-index: 5;
          }

          .page-break {
            background: #fff; margin-bottom: 20px; padding: 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15); border: 1px solid #ccc;
          }
          .page-break:last-child { margin-bottom: 0; }
        }

        /* =============================================
           INVOICE STYLING (DYNAMIC HEIGHT & COMPACT)
        ============================================= */
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }

        .marg-receipt-wrapper {
          font-family: var(--marg-font); color: #000; background: transparent; font-size: 11px;
        }
        .marg-receipt-wrapper * { box-sizing: border-box; }

        .marg-outer-box {
          border: var(--marg-border-thick);
          height: auto; 
          
          position: relative; display: flex; flex-direction: column;
          page-break-inside: avoid;
        }

        .marg-title-strip {
          background-color: #e0e0e0 !important; 
          border-bottom: var(--marg-border-thick);
          text-align: center; padding: 3px; font-weight: 900; font-size: 12px; letter-spacing: 1px;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }

        .marg-patient-box { display: flex; border-bottom: var(--marg-border-thick); }
        .marg-patient-left { width: 60%; border-right: var(--marg-border-thick); padding: 4px 8px; }
        .marg-patient-right { width: 40%; padding: 4px 8px; }
        .marg-patient-box table { width: 100%; font-size: 10px; line-height: 1.3; }
        .marg-patient-box table td { vertical-align: top; }

        /* ITEMS FLEX TABLE */
        .marg-table-container {
          display: flex; flex-direction: column;
        }

        .marg-table-header {
          display: flex; width: 100%; border-bottom: var(--marg-border);
          font-weight: bold; text-transform: uppercase; font-size: 9px; /* Reduced */
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }

        .marg-table-row { display: flex; width: 100%; font-size: 8px; } /* Reduced from 11px */

        /* ✅ EXACT WIDTHS & COMPACT PADDING */
        .m-col-sn    { width: 4%;  text-align: center; padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-desc  { width: 28%; text-align: left;   padding: 1px 3px; border-right: var(--marg-border); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .m-col-hsn   { width: 11%; text-align: center; padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-batch { width: 11%; text-align: center;   padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-pack  { width: 6%;  text-align: center; padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-qty   { width: 7%;  text-align: center; padding: 1px 3px; border-right: var(--marg-border); font-weight: bold; }
        .m-col-rate  { width: 4%;  text-align: right;  padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-tax   { width: 9%;  text-align: right;  padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-gst   { width: 5%;  text-align: right;  padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-dis   { width: 5%;  text-align: right;  padding: 1px 3px; border-right: var(--marg-border); }
        .m-col-amt   { width: 10%; text-align: right;  padding: 1px 3px; font-weight: bold; }

        .marg-page-total {
          display: flex; width: 100%;
          border-top: var(--marg-border); border-bottom: var(--marg-border-thick);
          font-weight: bold; font-size: 10px; background-color: #f9f9f9 !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }

        /* FOOTER SECTIONS */
        .marg-footer-grid { display: flex; border-top: var(--marg-border-thick); }
        .marg-words-box {
          width: 60%; border-right: var(--marg-border-thick); padding: 6px 10px;
          display: flex; flex-direction: column; justify-content: space-between;
        }
        .marg-calc-box { width: 40%; }
        .marg-calc-table { width: 100%; border-collapse: collapse; }
        .marg-calc-table td { padding: 0px 8px; border-bottom: var(--marg-border); font-size: 8px; } /* Reduced padding and font */
        .marg-calc-table tr:last-child td { border-bottom: none; }

        .marg-highlight-row td {
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
        }

        .marg-signature-box {
          display: flex; justify-content: space-between;
          padding: 15px 20px 5px 20px; border-top: var(--marg-border-thick); background: #fff;
        }
        .marg-sig-line {
          border-top: 1px solid #000; padding-top: 2px; text-align: center;
          font-weight: bold; font-size: 9px; width: 100%;
        }
      `}</style>

      {/* ── OVERLAY ── */}
      <div className="rm-overlay">

        {/* ── MODAL CARD ── */}
        <div className="rm-card">

          {/* ── HEADER (no-print) ── */}
          <div className="rm-header no-print">
            <h5 className="mb-0 fs-6 fw-bold">
              <i className="bi bi-receipt me-2"></i> Receipt Preview (Compact Format)
            </h5>
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          {/* ── SCROLLABLE INVOICE CONTENT (printed via this component's own @media print) ── */}
          <div className="rm-body printable-invoice">
            <div id="marg-print-section" className="marg-receipt-wrapper">
              
              {pages.map((page, index) => (
                <div key={index} className="page-break">
                  <div className="marg-outer-box">

                    {/* COMPANY DETAILS */}
                    <div className="d-flex border-bottom border-dark">
                      {company.logoBase64 && (
                        <div
                          className="p-2 d-flex justify-content-center align-items-center"
                          style={{ width: "80px", borderRight: "1px solid #000" }}
                        >
                          <img
                            src={`data:image/jpeg;base64,${company.logoBase64}`}
                            alt="Logo"
                            style={{ maxWidth: "100%", maxHeight: "40px", objectFit: "contain" }}
                          />
                        </div>
                      )}
                      <div className="p-2 flex-grow-1">
                        <h4 className="mb-0 fw-bold" style={{ fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {company.name || "COMPANY NAME"}
                        </h4>
                        <div style={{ fontSize: "9px" }}>
                          {company.address && (
                            <div>
                              {company.address}
                              {company.district && `, ${company.district}`}
                              {company.state && ` (${company.stateCode})`}
                            </div>
                          )}
                          <div>
                            {company.contact1 && `Ph: ${company.contact1} `}
                            {company.email && `| Email: ${company.email}`}
                          </div>
                          {company.gstNo && <div><strong>GSTIN:</strong> {company.gstNo}</div>}
                          {company.regNo && <div><strong>D.L. No:</strong> {company.regNo}</div>}
                        </div>
                      </div>
                      
                      {pages.length > 1 && (
                        <div className="p-2 text-end" style={{ fontSize: '9px', fontWeight: 'bold' }}>
                          Page {index + 1} of {pages.length}
                        </div>
                      )}
                    </div>

                    {/* INVOICE TITLE */}
                    <div className="marg-title-strip">TAX INVOICE</div>

                    {/* CUSTOMER & BILL DETAILS */}
                    <div className="marg-patient-box">
                      <div className="marg-patient-left">
                        <table>
                          <tbody>
                            <tr>
                              <td width="20%"><strong>To,</strong></td>
                              <td width="5%">:</td>
                              <td><strong style={{ fontSize: '11px' }}>{customer.name || "Cash Customer"}</strong></td>
                            </tr>
                            <tr>
                              <td><strong>Address</strong></td>
                              <td>:</td>
                              <td>{customer.address || "-"}</td>
                            </tr>
                            <tr>
                              <td><strong>Info</strong></td>
                              <td>:</td>
                              <td>
                                {customer.mobileNo && `Mob: ${customer.mobileNo} `}
                                {customer.gender && `| ${customer.gender} `}
                                {customer.age && `| Age: ${customer.age}`}
                              </td>
                            </tr>
                            {receipt.referredBy && (
                              <tr>
                                <td><strong>Referred By</strong></td>
                                <td>:</td>
                                <td>{receipt.referredBy}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    <div className="marg-patient-right">
  {/* width: "auto" aur marginLeft: "auto" table ko ekdam right me dhakel dega */}
  <table style={{ width: "auto", marginLeft: "auto", fontSize: "10px" }}>
    <tbody>
      <tr>
        <td style={{ paddingBottom: "2px", textAlign: "right", paddingRight: "5px" }}><strong>Invoice No</strong></td>
        <td style={{ paddingBottom: "2px" }}>: <span className="fw-bold">{receipt.billNo}</span></td>
      </tr>
      <tr>
        <td style={{ paddingBottom: "2px", textAlign: "right", paddingRight: "5px" }}><strong> Invoice Date</strong></td>
        <td style={{ paddingBottom: "2px" }}>: {formatDate(receipt.billDate)}</td>
      </tr>
      <tr>
        <td style={{ paddingBottom: "2px", textAlign: "right", paddingRight: "5px" }}><strong>Time</strong></td>
        <td style={{ paddingBottom: "2px" }}>: {formatIndianTime(receipt.billDate)}</td>
      </tr>
    </tbody>
  </table>
</div>
                    </div>

                    {/* ITEM TABLE */}
                    <div className="marg-table-container">
                      <div className="marg-table-header">
                        <div className="m-col-sn">SN</div>
                        <div className="m-col-desc">Product Description</div>
                        <div className="m-col-hsn">HSN</div>
                        <div className="m-col-batch">Batch</div>
                        <div className="m-col-pack">Pack</div>
                        <div className="m-col-qty">Qty</div>
                        <div className="m-col-rate">Rate</div>
                        <div className="m-col-tax">Taxable</div>
                        <div className="m-col-gst">GST%</div>
                        <div className="m-col-dis">Dis</div>
                        <div className="m-col-amt" style={{ borderRight: "none" }}>Net Amt</div>
                      </div>
                      
                      {page.pageNumber > 1 && (
                        <div className="marg-page-total" style={{ borderTop: 'none', borderBottom: 'var(--marg-border)' }}>
                          <div style={{ width: '90%', textAlign: 'right', padding: '2px 8px' }}>Brought Forward (B/F):</div>
                          <div className="m-col-amt" style={{ borderRight: "none", padding: '2px 3px' }}>{page.previousTotal.toFixed(2)}</div>
                        </div>
                      )}

                      {page.items.map((it, idx) => {
                        const serialNo = (page.pageNumber - 1) * ITEMS_PER_PAGE + idx + 1;
                        const rate = Number(it.rate || 0);
                        const taxable = Number(it.taxable || 0);
                        const netAmt = Number(it.netAmount || 0);
                        const dis = Number(it.itemDiscount || 0);
                        const gstP = Number(it.gstP || 0);
                        const formattedQty = formatQty(it.leafQty, it.pcQty);

                        return (
                          <div className="marg-table-row" key={idx}>
                            <div className="m-col-sn">{serialNo}</div>
                            <div className="m-col-desc" title={it.medicine}>{it.medicine}</div>
                            <div className="m-col-hsn">{it.hsnCode || "-"}</div>
                            <div className="m-col-batch">{it.batchNo || "-"}</div>
                            <div className="m-col-pack">{it.packSize ? `1*${it.packSize}` : "-"}</div>
                            <div className="m-col-qty">{formattedQty}</div>
                            <div className="m-col-rate">{rate.toFixed(2)}</div>
                            <div className="m-col-tax">{taxable.toFixed(2)}</div>
                            <div className="m-col-gst">{gstP > 0 ? `${gstP}%` : '-'}</div>
                            <div className="m-col-dis">{dis > 0 ? dis.toFixed(2) : '-'}</div>
                            <div className="m-col-amt" style={{ borderRight: "none" }}>{netAmt.toFixed(2)}</div>
                          </div>
                        );
                      })}
                    </div>

                    {!page.isLastPage && (
                      <div className="marg-page-total" style={{ borderTop: 'none' }}>
                        <div style={{ width: '90%', textAlign: 'right', padding: '2px 8px' }}>Carry Forward (C/F):</div>
                        <div className="m-col-amt" style={{ borderRight: "none", padding: '2px 3px' }}>{page.currentTotal.toFixed(2)}</div>
                      </div>
                    )}

                    {/* FOOTER TOTALS — last page only */}
                    {page.isLastPage && (
                      <div className="marg-footer-grid">
                        <div className="marg-words-box">
                          <div>
                            <div className="mb-1" style={{ fontSize: "10px" }}>
                              <strong>Amount in Words: </strong> <br/>
                              {numberToWords(receipt.netAmount)}
                            </div>
                            <div className="mt-1 text-muted" style={{ fontSize: "8px" }}>
                              <strong>Terms & Conditions:</strong><br/>
                              1. Goods once sold will not be taken back or exchanged.<br/>
                              2. Subject to local jurisdiction only.
                            </div>
                          </div>
                          
                          <div className=" mt-1">
                             <div style={{ borderTop: "1px dashed #000", display: "inline-block", paddingTop: "2px", fontSize: "9px" }}>
                               Customer Signature
                             </div>
                            
                          </div>
                        </div>
                        
                        <div className="marg-calc-box">
                          <table className="marg-calc-table">
                            <tbody>
                              <tr>
                                <td className="p-1 border-bottom px-2">Gross Amount:</td>
                                <td className="p-1 border-bottom text-end px-2">{(receipt.grossAmount ?? 0).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td className="p-1 border-bottom px-2">
                                  {receipt.discountValue > 0 
                                    ? `Total Discount ${receipt.discountType === 'PERCENT' ? `(${Number(receipt.discountValue)}%)` : '(₹)'}:` 
                                    : 'Total Discount:'}
                                </td>
                                <td className="p-1 border-bottom text-end px-2 text-danger">
                                  {(receipt.discountAmount ?? 0).toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <td className="p-1 border-bottom px-2">CGST + SGST:</td>
                                <td className="p-1 border-bottom text-end px-2">{((receipt.cgst ?? 0) + (receipt.sgst ?? 0)).toFixed(2)}</td>
                              </tr>
                              <tr style={{ backgroundColor: "#e9ecef" }}>
                                <td className="p-1 border-bottom px-2"><strong style={{ fontSize: "8px" }}>Net Payable:</strong></td>
                                <td className="p-1 border-bottom text-end px-2"><strong style={{ fontSize: "8px" }}>₹ {(receipt.netAmount ?? 0).toFixed(2)}</strong></td>
                              </tr>
                              <tr>
                                <td className="p-1 border-bottom px-2 text-success"><strong>Paid Amount:</strong></td>
                                <td className="p-1 border-bottom text-end px-2 text-success"><strong>{(receipt.paidAmount ?? 0).toFixed(2)}</strong></td>
                              </tr>
                              <tr>
                                <td className="p-1 border-bottom px-2 text-danger"><strong>Due Amount:</strong></td>
                                <td className="p-1 border-bottom text-end px-2 text-danger"><strong>{(receipt.dueAmount ?? 0).toFixed(2)}</strong></td>
                              </tr>
                              <tr>
                                <td colSpan="2" className="text-center align-bottom pb-1" style={{ height: "25px" }}>
                                  <div style={{ borderTop: "1px solid #000", margin: "0 auto", width: "85%", paddingTop: "2px", fontSize: "9px", fontWeight: "bold" }}>
                                    Authorized Signatory
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── ACTION BUTTONS (no-print) ── */}
          <div className="rm-footer no-print">
            <button className="btn btn-secondary btn-sm px-3" onClick={onClose}>
              <i className="bi bi-x-circle me-1"></i> Close
            </button>
            <button className="btn btn-primary btn-sm px-4 fw-bold" onClick={handlePrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
              <i className="bi bi-printer me-2"></i> Print Bill
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default MedicineSaleReceiptModal;